import { useParams } from "@tanstack/react-router";
import {
  Bar,
  BarChart,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import type {
  AuthDiagnosis,
  AuthSeverity,
  IpFailureSample,
  IpReputationCheck,
} from "@rua-watch/shared";
import {
  diagnoseDkimAuth,
  diagnoseSpfAuth,
  dominantFailureReason,
} from "@rua-watch/shared";
import { useIpDetail } from "../api/hooks";
import { formatInt, fromUnix } from "../lib/format";

export function IpDetailRoute() {
  const { ip } = useParams({ strict: false }) as { ip: string };
  const { data, isLoading, error } = useIpDetail(ip);

  if (isLoading) return <p className= "text-slate-500 dark:text-slate-400">Loading…</p>;
  if (error || !data) return <p className= "text-red-700 dark:text-red-300">Not found.</p>;

  return (
    <div className= "space-y-6">
      <div>
        <div className= "text-xs uppercase text-slate-500 dark:text-slate-400">IP</div>
        <h1 className= "font-mono text-xl font-semibold text-slate-900 dark:text-slate-100">{data.ip}</h1>
      </div>

      <div className= "grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
          <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Enrichment</h2>
          <dl className= "mt-2 grid grid-cols-3 gap-y-2 text-sm">
            <dt className= "text-slate-500 dark:text-slate-400">PTR</dt><dd className= "col-span-2 font-mono">{data.ptr ?? "—"}</dd>
            <dt className= "text-slate-500 dark:text-slate-400">ASN</dt><dd className= "col-span-2">AS{data.asn ?? "?"} {data.asnOrg ?? ""}</dd>
            <dt className= "text-slate-500 dark:text-slate-400">Country</dt><dd className= "col-span-2">{data.country ?? "—"}</dd>
            <dt className= "text-slate-500 dark:text-slate-400">Classification</dt>
            <dd className= "col-span-2">
              <div className= "flex flex-wrap items-center gap-1.5">
                <span>{data.classification}</span>
                {data.kind === "forwarder" ? (
                  <span
                    title= "Auto-forwarding pattern: mailbox provider relaying DKIM-signed mail (SPF fails because the forwarder is not in original sender's SPF). Not spoofing."
                    className= "rounded bg-sky-100 dark:bg-sky-900/30 px-2 py-0.5 text-xs font-medium text-sky-800 dark:text-sky-300"
                  >
                    Forward
                  </span>
                ) : null}
              </div>
            </dd>
          </dl>
        </div>

        <div className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
          <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Volume (last 90d)</h2>
          <div className= "h-56">
            <ResponsiveContainer width= "100%" height= "100%">
              <BarChart data={[...data.history].reverse()}>
                <CartesianGrid strokeDasharray= "3" stroke= "#e2e8f0" />
                <XAxis dataKey= "day" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip />
                <Bar dataKey= "messages" fill= "#0f172a" />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </div>
      </div>

      <div className= "overflow-hidden rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
        <table className= "min-w-full text-sm">
          <thead className= "bg-slate-50 dark:bg-slate-800/60 text-left text-slate-600 dark:text-slate-400">
            <tr>
              <th className= "px-4 py-2 font-medium">Day</th>
              <th className= "px-4 py-2 font-medium text-right">Messages</th>
              <th className= "px-4 py-2 font-medium text-right">DMARC pass</th>
              <th className= "px-4 py-2 font-medium">Worst disposition</th>
            </tr>
          </thead>
          <tbody className= "divide-y divide-slate-100 dark:divide-slate-800">
            {data.history.map((d) => (
              <tr key={d.day}>
                <td className= "px-4 py-2">{d.day}</td>
                <td className= "px-4 py-2 text-right tabular-nums">{formatInt(d.messages)}</td>
                <td className= "px-4 py-2 text-right tabular-nums">{formatInt(d.dmarcPass)}</td>
                <td className= "px-4 py-2">{d.disposition}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <ReputationCard checks={data.reputation} />

      <FailureSamples samples={data.recentFailureSamples} />
    </div>
  );
}

function ReputationCard({ checks }: { checks: IpReputationCheck[] }) {
  if (checks.length === 0) {
    // Outside the top-N volume cutoff — common for tail IPs. Don't
    // surface a noisy empty card; operators only care once the IP is
    // big enough to have been swept.
    return null;
  }
  const anyListed = checks.some((c) => c.listed);
  return (
    <div className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className= "flex items-center justify-between border-b border-slate-200 dark:border-slate-800 px-4 py-2">
        <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Reputation</h2>
        <span
          className={`rounded px-2 py-0.5 text-xs font-medium ${
            anyListed
              ?"bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
              :"bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300"
          }`}
        >
          {anyListed
            ?`Listed in ${checks.filter((c) => c.listed).length} of ${checks.length}`
            :`Clean (${checks.length} checked) `}
        </span>
      </div>
      <table className= "min-w-full text-sm">
        <thead className= "bg-slate-50 dark:bg-slate-800/60 text-left text-slate-600 dark:text-slate-400">
          <tr>
            <th className= "px-4 py-2 font-medium">Source</th>
            <th className= "px-4 py-2 font-medium">Status</th>
            <th className= "px-4 py-2 font-medium">Category</th>
            <th className= "px-4 py-2 font-medium">Last checked</th>
            <th className= "px-4 py-2 font-medium text-right">Action</th>
          </tr>
        </thead>
        <tbody className= "divide-y divide-slate-100 dark:divide-slate-800">
          {checks.map((c) => (
            <tr key={c.source}>
              <td className= "px-4 py-2">{c.sourceLabel}</td>
              <td className= "px-4 py-2">
                {c.listed ? (
                  <span className= "rounded bg-red-100 dark:bg-red-900/30 px-2 py-0.5 text-xs font-medium text-red-800 dark:text-red-300">
                    listed{c.returnCode ?` (${c.returnCode}) ` :""}
                  </span>
                ) : (
                  <span className= "rounded bg-emerald-100 dark:bg-emerald-900/30 px-2 py-0.5 text-xs font-medium text-emerald-800 dark:text-emerald-300">
                    clean
                  </span>
                )}
              </td>
              <td className= "px-4 py-2 text-xs text-slate-600 dark:text-slate-400">
                {c.category ?? <span className= "text-slate-400 dark:text-slate-500">—</span>}
              </td>
              <td className= "px-4 py-2 text-xs text-slate-600 dark:text-slate-400">{fromUnix(c.checkedAt)}</td>
              <td className= "px-4 py-2 text-right">
                {c.listed && c.delistingUrl ? (
                  <a
                    href={c.delistingUrl}
                    target= "_blank"
                    rel= "noreferrer"
                    className= "rounded border border-slate-300 dark:border-slate-700 px-2 py-1 text-xs text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800"
                  >
                    Delisting →
                  </a>
                ) : (
                  <span className= "text-xs text-slate-400 dark:text-slate-500">—</span>
                )}
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function FailureSamples({ samples }: { samples: IpFailureSample[] }) {
  return (
    <div className= "rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
      <div className= "border-b border-slate-200 dark:border-slate-800 px-4 py-2 flex items-center justify-between">
        <h2 className= "text-sm font-medium text-slate-700 dark:text-slate-300">Recent failure samples</h2>
        <span className= "text-xs text-slate-500 dark:text-slate-400">{samples.length}</span>
      </div>
      {samples.length === 0 ? (
        <p className= "px-4 py-6 text-center text-sm text-slate-500 dark:text-slate-400">
          No DMARC failures recorded for this IP.
        </p>
      ) : (
        <ul className= "divide-y divide-slate-100 dark:divide-slate-800">
          {samples.map((s, i) => (
            <li key={`${s.reportId}-${i}`} className= "p-4 space-y-2">
              <div className= "flex flex-wrap items-center gap-x-3 gap-y-1 text-xs text-slate-500 dark:text-slate-400">
                <span>
                  <span className= "text-slate-400 dark:text-slate-500">Reported by</span>{""}
                  <span className= "font-medium text-slate-700 dark:text-slate-300">{s.orgName}</span>
                </span>
                <span>
                  <span className= "text-slate-400 dark:text-slate-500">on</span> {s.day}
                </span>
                <span>
                  <span className= "text-slate-400 dark:text-slate-500">domain</span>{""}
                  <span className= "font-mono text-slate-700 dark:text-slate-300">{s.domain}</span>
                </span>
                <span>
                  <span className= "text-slate-400 dark:text-slate-500">count</span>{""}
                  <span className= "tabular-nums text-slate-700 dark:text-slate-300">{formatInt(s.count)}</span>
                </span>
                <DispoPill p={s.disposition} />
              </div>

              <div className= "flex flex-wrap gap-2 text-xs">
                <Badge label= "DKIM aligned" value={s.dkimAligned} />
                <Badge label= "SPF aligned" value={s.spfAligned} />
                {s.headerFrom ? (
                  <span className= "rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 font-mono text-slate-600 dark:text-slate-400">
                    From: {s.headerFrom}
                  </span>
                ) : null}
                {s.envelopeFrom ? (
                  <span className= "rounded bg-slate-100 dark:bg-slate-800 px-2 py-0.5 font-mono text-slate-600 dark:text-slate-400">
                    Envelope: {s.envelopeFrom}
                  </span>
                ) : null}
              </div>

              {s.dkimResults.length > 0 || s.spfResults.length > 0 ? (
                <DiagnosisBlock sample={s} />
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DispoPill({ p }: { p:"none" |"quarantine" |"reject" }) {
  const color =
    p === "reject"
      ?"bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300"
      : p === "quarantine"
      ?"bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300"
      :"bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300";
  return <span className={`rounded px-2 py-0.5 text-[10px] uppercase ${color}`}>{p}</span>;
}

function Badge({ label, value }: { label: string; value:"pass" |"fail" | null }) {
  const color = value === "pass" ?"bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300" :"bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300";
  return (
    <span className={`rounded px-2 py-0.5 ${color}`}>
      {label}: {value ?? "—"}
    </span>
  );
}

// Renders the auth_results detail with per-row diagnoses + a top-line
// summary picking the dominant cause. The summary is what an operator
// glances at to answer"why did this fail?"; the per-row list lets
// them dig into each SPF/DKIM check (e.g. when an ESP signs with two
// d= values, only one of which aligns).
function DiagnosisBlock({ sample }: { sample: IpFailureSample }) {
  const dominant = dominantFailureReason(
    sample.spfResults,
    sample.dkimResults,
    sample.headerFrom,
  );
  return (
    <div className= "space-y-1.5">
      {dominant ? (
        <div className= "rounded border border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/60 p-2 text-xs">
          <span className= "text-slate-500 dark:text-slate-400">Likely cause</span>{" "}
          <span className= "font-medium text-slate-700 dark:text-slate-200 uppercase">{dominant.source}</span>
          {" — "}
          <DiagnosisPill d={dominant.diagnosis} />
          <p className= "mt-1 text-slate-600 dark:text-slate-400">{dominant.diagnosis.hint}</p>
        </div>
      ) : null}

      <div className= "rounded bg-slate-50 dark:bg-slate-800/60 p-2 space-y-1">
        {sample.dkimResults.map((d, idx) => {
          const dx = diagnoseDkimAuth(d, sample.headerFrom);
          return (
            <div key={`d-${idx}`} className= "flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
              <span className= "text-slate-400 dark:text-slate-500">DKIM</span>
              <span className= "font-mono">{d.domain ||"?"}</span>
              {d.selector ? <span className= "text-slate-400 dark:text-slate-500">[{d.selector}]</span> : null}
              <span className= "text-slate-400 dark:text-slate-500">→</span>
              <DiagnosisPill d={dx} />
            </div>
          );
        })}
        {sample.spfResults.map((p, idx) => {
          const dx = diagnoseSpfAuth(p, sample.headerFrom);
          return (
            <div key={`s-${idx}`} className= "flex flex-wrap items-center gap-x-2 gap-y-1 text-xs text-slate-600 dark:text-slate-400">
              <span className= "text-slate-400 dark:text-slate-500">SPF</span>
              <span className= "font-mono">{p.domain ||"?"}</span>
              {p.scope ? <span className= "text-slate-400 dark:text-slate-500">[{p.scope}]</span> : null}
              <span className= "text-slate-400 dark:text-slate-500">→</span>
              <DiagnosisPill d={dx} />
            </div>
          );
        })}
      </div>
    </div>
  );
}

const SEVERITY_CLASSES: Record<AuthSeverity, string> = {
  ok: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-800 dark:text-emerald-300",
  info: "bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300",
  warn: "bg-amber-100 dark:bg-amber-900/30 text-amber-800 dark:text-amber-300",
  error: "bg-red-100 dark:bg-red-900/30 text-red-800 dark:text-red-300",
};

function DiagnosisPill({ d }: { d: AuthDiagnosis }) {
  return (
    <span
      title={d.hint}
      className={`rounded px-2 py-0.5 text-[11px] font-medium ${SEVERITY_CLASSES[d.severity]}`}
    >
      {d.label}
    </span>
  );
}
